import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

export interface PatientInviteForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ProviderInviteForm {
  firstName: string;
  lastName: string;
  email: string;
  credentials: string;
  specialty: string;
}

export interface CareAssignmentForm {
  patientId: string;
  providerId: string;
  role: string;
}

interface PatientRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
}

interface ProviderRecord {
  id: string;
  firstName: string;
  lastName: string;
  credentials: string;
  assignedPatients: number;
  maxCapacity: number;
  status: string;
}

// ─── Invite Patient Dialog ───────────────────────────────────────────────────

interface InvitePatientDialogProps {
  open: boolean;
  patientInvite: PatientInviteForm;
  setPatientInvite: (val: PatientInviteForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const InvitePatientDialog = ({
  open,
  patientInvite,
  setPatientInvite,
  onClose,
  onSubmit,
}: InvitePatientDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Invite Patient to Organization</DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            required
            value={patientInvite.firstName}
            onChange={(e) => setPatientInvite({ ...patientInvite, firstName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            required
            value={patientInvite.lastName}
            onChange={(e) => setPatientInvite({ ...patientInvite, lastName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={patientInvite.email}
            onChange={(e) => setPatientInvite({ ...patientInvite, email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={patientInvite.phone}
            onChange={(e) => setPatientInvite({ ...patientInvite, phone: e.target.value })}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        disabled={!patientInvite.firstName || !patientInvite.lastName || !patientInvite.email}
      >
        Send Invitation
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Invite Provider Dialog ──────────────────────────────────────────────────

interface InviteProviderDialogProps {
  open: boolean;
  providerInvite: ProviderInviteForm;
  setProviderInvite: (val: ProviderInviteForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const InviteProviderDialog = ({
  open,
  providerInvite,
  setProviderInvite,
  onClose,
  onSubmit,
}: InviteProviderDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Invite Care Provider to Organization</DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            required
            value={providerInvite.firstName}
            onChange={(e) => setProviderInvite({ ...providerInvite, firstName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            required
            value={providerInvite.lastName}
            onChange={(e) => setProviderInvite({ ...providerInvite, lastName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={providerInvite.email}
            onChange={(e) => setProviderInvite({ ...providerInvite, email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Credentials"
            placeholder="e.g., RD, CDE"
            value={providerInvite.credentials}
            onChange={(e) => setProviderInvite({ ...providerInvite, credentials: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Specialty"
            placeholder="e.g., Clinical Nutrition"
            value={providerInvite.specialty}
            onChange={(e) => setProviderInvite({ ...providerInvite, specialty: e.target.value })}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        disabled={!providerInvite.firstName || !providerInvite.lastName || !providerInvite.email}
      >
        Send Invitation
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Assign Care Team Dialog ─────────────────────────────────────────────────

interface AssignCareDialogProps {
  open: boolean;
  careAssignment: CareAssignmentForm;
  setCareAssignment: (val: CareAssignmentForm) => void;
  patients: PatientRecord[];
  providers: ProviderRecord[];
  onClose: () => void;
  onSubmit: () => void;
}

export const AssignCareDialog = ({
  open,
  careAssignment,
  setCareAssignment,
  patients,
  providers,
  onClose,
  onSubmit,
}: AssignCareDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Assign Care Provider to Patient</DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Patient</InputLabel>
            <Select
              value={careAssignment.patientId}
              label="Patient"
              onChange={(e) => setCareAssignment({ ...careAssignment, patientId: e.target.value })}
            >
              {patients
                .filter((p) => p.status === 'ACTIVE')
                .map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName} ({patient.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Care Provider</InputLabel>
            <Select
              value={careAssignment.providerId}
              label="Care Provider"
              onChange={(e) =>
                setCareAssignment({ ...careAssignment, providerId: e.target.value })
              }
            >
              {providers
                .filter((p) => p.status === 'ACTIVE')
                .map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.firstName} {provider.lastName} - {provider.credentials} (
                    {provider.assignedPatients}/{provider.maxCapacity} patients)
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Role</InputLabel>
            <Select
              value={careAssignment.role}
              label="Role"
              onChange={(e) => setCareAssignment({ ...careAssignment, role: e.target.value })}
            >
              <MenuItem value="PRIMARY_NUTRITIONIST">Primary Nutritionist</MenuItem>
              <MenuItem value="CONSULTING_NUTRITIONIST">Consulting Nutritionist</MenuItem>
              <MenuItem value="ENDOCRINOLOGIST">Endocrinologist</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        disabled={!careAssignment.patientId || !careAssignment.providerId}
      >
        Assign Provider
      </Button>
    </DialogActions>
  </Dialog>
);
